import { useEffect, useRef } from "react";
import { sendTrackingTime } from "src/api/trackingTime";
import { getAccessToken, setTimeOnSiteCookie } from "@lib/shared/cookies";

export const useTimeOnSite = (
    initialTimeOnSite: string,
    disableTimeOnSite: boolean = false,
) => {
    const isActive = useRef<boolean>(true);
    const startTime = useRef<number>(0);
    const totalTimeSpendOnSite = useRef<number>(
        Number(initialTimeOnSite) * 1000,
    );
    const prevTotalTimeSpendOnSite = useRef<number>(
        totalTimeSpendOnSite.current,
    );

    // function to track time
    const trackTime = () => {
        if (isActive && !disableTimeOnSite) {
            totalTimeSpendOnSite.current += Date.now() - startTime.current;
            startTime.current = Date.now();
        }
    };

    // function to start tracking time
    const startTrackingTime = () => {
        if (!disableTimeOnSite) {
            isActive.current = true;
            startTime.current = Date.now();
            trackTime();
        }
    };

    // function to stop tracking time
    const stopTrackingTime = () => {
        isActive.current = false;
        totalTimeSpendOnSite.current += Date.now() - startTime.current;
    };

    if (!disableTimeOnSite) startTrackingTime();

    const onVisibilityChange = () => {
        if (document.visibilityState === "visible") startTrackingTime();
        else {
            stopTrackingTime();
        }
    };

    const beforeUnload = () => {
        stopTrackingTime();
        const accessToken = getAccessToken();
        if (
            accessToken &&
            prevTotalTimeSpendOnSite.current !== totalTimeSpendOnSite.current &&
            totalTimeSpendOnSite.current / 1000 > 10 &&
            process.env.NODE_ENV !== "development"
        ) {
            prevTotalTimeSpendOnSite.current = totalTimeSpendOnSite.current;
            setTimeOnSiteCookie(totalTimeSpendOnSite.current / 1000);
            void sendTrackingTime({
                event: "timeOnSite",
                eventValue: (totalTimeSpendOnSite.current / 1000).toString(),
                eventCategory: "meta",
            });
        }
    };

    useEffect(() => {
        window.setInterval(function () {
            if (document.visibilityState === "visible") {
                trackTime();
            }
        }, 1000);

        window.setInterval(() => {
            const token = getAccessToken();
            if (
                token &&
                prevTotalTimeSpendOnSite.current !==
                    totalTimeSpendOnSite.current &&
                process.env.NODE_ENV !== "development"
            ) {
                prevTotalTimeSpendOnSite.current = totalTimeSpendOnSite.current;
                void sendTrackingTime({
                    event: "timeOnSite",
                    eventValue: (
                        totalTimeSpendOnSite.current / 1000
                    ).toString(),
                    eventCategory: "meta",
                });
            }
        }, 10000);

        window.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("beforeunload", beforeUnload);

        return () => {
            window.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("beforeunload", beforeUnload);
        };
    }, []);

    return {
        trackTime,
        startTrackingTime,
        stopTrackingTime,
        totalTimeSpendOnSite,
        prevTotalTimeSpendOnSite,
    };
};
